// @ts-nocheck
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../../../components/layout';
import EntityGuard from '../../../components/EntityGuard';
import Header from '../../../components/header';
import { AgGridReact } from 'ag-grid-react';
import Pagination from '../../../components/pagination';
import Loader from '../../../components/loader';
import { titleCell } from '../../../components/elements/function';
import { PAGE_SIZE } from '../../../_common/constant';
import { useAssetsApi } from '../../../store/api/hooks/assetsApiHook';
import { useSpearPartsApi } from '../../../store/api/hooks/spearPartsApiHook';
import { useCommonApi } from '../../../store/api/hooks/commonApiHook';
import Icon from '../../../components/icons';
import Filters from '../../../components/filters';
import { filterConfigType } from '../../../_config/types';
import Button from '../../../components/elements/button';
import { downloadExcel } from '../../../_common/functions';
import toast from 'react-hot-toast';

function currencyCellRenderer(params: any) {
  const val = params.value;
  if (val == null) return '--';
  return `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const SparePartsUsageReport: React.FC = () => {
  const assetsApi = useAssetsApi();
  const spearPartsAPI = useSpearPartsApi();
  const commonApi: any = useCommonApi();
  const filtersRef: any = useRef();

  const [rowData, setRowData] = useState<any>(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [summary, setSummary] = useState<any>(null);
  const [facilityOptions, setFacilityOptions] = useState<any[]>([]);
  const [subFacilityOptions, setSubFacilityOptions] = useState<any[]>([]);
  const [assetGroupOptions, setAssetGroupOptions] = useState<any[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  const loadPartsOptions = (searchParam: string): Promise<any> => {
    return new Promise((resolve) => {
      const payload = { pageSize: 50, pageNumber: 1, searchParam };
      spearPartsAPI.getSpearPartsList(
        payload,
        (response: any) => {
          const options = response.sparePartsList?.map((part: any) => ({
            label: `${part.partsCode} - ${part.sparePartsName}`,
            value: part.partsCode,
          })) || [];
          resolve(options);
        },
        () => resolve([])
      );
    });
  };

  const loadCompletedByOptions = (searchParam: string): Promise<any> => {
    return new Promise((resolve) => {
      const payload: any = {};
      if (searchParam !== '') {
        payload.searchParam = searchParam;
        payload.pageSize = 0;
      }
      commonApi.GetUserList(payload, (resp: any) => {
        resolve(resp.userList ? resp.userList.map((user: any) => ({
          label: `${user.firstName} ${user.lastName}`,
          value: user.userID,
        })) : []);
      }, () => resolve([]));
    });
  };

  const filterConfig: filterConfigType[] = [
    { name: 'Facility', type: 'dropdown', key: 'facility', options: facilityOptions, isMulti: true },
    { name: 'Sub Facility', type: 'dropdown', key: 'subFacility', options: subFacilityOptions, isMulti: true },
    { name: 'Asset Group', type: 'dropdown', key: 'assetGroup', options: assetGroupOptions, isMulti: true },
    { name: 'Department', type: 'dropdown', key: 'department', options: departmentOptions, isMulti: true },
    { name: 'Part Name', type: 'dropdown', key: 'partsCodes', async: loadPartsOptions, isMulti: true },
    { name: 'Used By', type: 'dropdown', key: 'completedByIDs', async: loadCompletedByOptions, isMulti: true },
    { name: 'Start Date', type: 'date', key: 'startDate' },
    { name: 'End Date', type: 'date', key: 'endDate' },
  ];

  const fetchData = useCallback((pageNum: number, filterParams: Record<string, any> = {}) => {
    setRowData(null);
    const params: any = { PageSize: PAGE_SIZE, PageNumber: pageNum };
    if (filterParams.facility)        params.FacilityIDs    = filterParams.facility;
    if (filterParams.subFacility)     params.SubFacilityIDs = filterParams.subFacility;
    if (filterParams.assetGroup)      params.AssetGroupIDs  = filterParams.assetGroup;
    if (filterParams.department)      params.DepartmentIDs  = filterParams.department;
    if (filterParams.startDate)       params.StartDate      = filterParams.startDate;
    if (filterParams.endDate)         params.EndDate        = filterParams.endDate;
    if (filterParams.partsCodes)      params.PartsCodes     = filterParams.partsCodes;
    if (filterParams.completedByIDs)  params.CompletedByIDs = filterParams.completedByIDs;

    assetsApi.getSparePartsUsageReport(
      params,
      (response: any) => {
        const d = response?.data || response;
        setRowData(d?.rows || []);
        setSummary(d?.summary);
        setTotalRecords(d?.totalRecord || 0);
        setFacilityOptions((d?.facilities || []).map((f: any) => ({ value: String(f.id), label: f.name })));
        setSubFacilityOptions((d?.subFacilities || []).filter((f: any) => f.name?.trim()).map((f: any) => ({ value: String(f.id), label: f.name })));
        setAssetGroupOptions((d?.assetGroups || []).map((f: any) => ({ value: String(f.id), label: f.name })));
        setDepartmentOptions((d?.departments || []).map((f: any) => ({ value: String(f.id), label: f.name })));
      },
      (error: any) => { console.error('Failed to fetch spare parts usage report:', error); setRowData([]); }
    );
  }, []);

  useEffect(() => { fetchData(currentPage, filters); }, [currentPage, filters]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  const handleExport = () => {
    const params: any = { PageSize: null, PageNumber: null };
    if (filters.facility)        params.FacilityIDs    = filters.facility;
    if (filters.subFacility)     params.SubFacilityIDs = filters.subFacility;
    if (filters.assetGroup)      params.AssetGroupIDs  = filters.assetGroup;
    if (filters.department)      params.DepartmentIDs  = filters.department;
    if (filters.startDate)       params.StartDate      = filters.startDate;
    if (filters.endDate)         params.EndDate        = filters.endDate;
    if (filters.partsCodes)      params.PartsCodes     = filters.partsCodes;
    if (filters.completedByIDs)  params.CompletedByIDs = filters.completedByIDs;
    setIsExporting(true);
    assetsApi.exportSparePartsUsageReportToXLS(
      params,
      (resp: any) => { downloadExcel(resp, 'spare_parts_usage_report'); toast.success('Exported successfully!'); setIsExporting(false); },
      (error: any) => { toast.error(error || 'Export failed'); setIsExporting(false); }
    );
  };

  const columnDefs = [
    { headerName: 'Part Name',    field: 'partsName',      headerClass: 'bg-[#f0f0f0]', minWidth: 200, flex: 1, cellRenderer: titleCell, headerTooltip: 'Part Name' },
    { headerName: 'Part Code',    field: 'partsCode',      headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: titleCell, headerTooltip: 'Part Code' },
    { headerName: 'Facility',     field: 'facilityName',   headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Facility' },
    { headerName: 'Sub Facility', field: 'subFacilityName',headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Sub Facility' },
    { headerName: 'Asset Group',  field: 'assetGroupName', headerClass: 'bg-[#f0f0f0]', width: 150, cellRenderer: titleCell, headerTooltip: 'Asset Group' },
    { headerName: 'Department',   field: 'departmentName', headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Department' },
    { headerName: 'Date Used',    field: 'dateUsed',       headerClass: 'bg-[#f0f0f0]', width: 130, cellRenderer: titleCell, headerTooltip: 'Date Used' },
    { headerName: 'Qty Used',     field: 'usedQty',        headerClass: 'bg-[#f0f0f0]', width: 110, type: 'rightAligned', headerTooltip: 'Quantity Used' },
    { headerName: 'Unit Price',   field: 'unitPrice',      headerClass: 'bg-[#f0f0f0]', width: 120, type: 'rightAligned', cellRenderer: currencyCellRenderer, headerTooltip: 'Unit Price' },
    { headerName: 'Net Total',    field: 'netTotal',       headerClass: 'bg-[#f0f0f0]', width: 130, type: 'rightAligned', cellRenderer: currencyCellRenderer, headerTooltip: 'Net Total' },
    { headerName: 'Work Order',   field: 'workOrderCode',  headerClass: 'bg-[#f0f0f0]', width: 140, cellRenderer: titleCell, headerTooltip: 'Work Order' },
    { headerName: 'Asset Name',   field: 'assetName',      headerClass: 'bg-[#f0f0f0]', width: 180, cellRenderer: titleCell, headerTooltip: 'Asset Name' },
    { headerName: 'Used By',      field: 'usedByName',     headerClass: 'bg-[#f0f0f0]', width: 160, cellRenderer: titleCell, headerTooltip: 'Used By' },
  ];

  return (
    <Layout>
      <EntityGuard entity="Spare Parts Usage Report">
      <Header title="Spare Parts Usage Report" back="/reports">
        <Header.Right>
          <Button theme='primary' onClick={handleExport} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
        </Header.Right>
      </Header>
      <div className="p-4 pb-0">
        {summary && (
          <div className="bg-white rounded-lg mb-4 flex items-center gap-6 flex-wrap text-[13px]" style={{ padding: '12px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            <span style={{ color: '#111827', fontWeight: 600 }}>
              Total Parts Used: {Number(summary.totalQtyUsed || 0).toLocaleString('en-US')} units
            </span>
            <span className="text-gray-400">&middot;</span>
            <span style={{ color: '#2B5A2E', fontWeight: 600 }}>
              Total Value: ${Number(summary.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        )}
      </div>
      <div className='bg-[#FAFAFA]'>
        <div className='flex gap-4 px-5 py-2 items-center'>
          <Button theme='light' onClick={() => filtersRef.current.showModal()}>Filter <Icon name='filter' size={1.40} className={hasActiveFilters ? "text-blue-700" : ""} /></Button>
          {hasActiveFilters && (
            <p className='text-primary font-semibold text-sm cursor-pointer' onClick={() => setFilters({})}>Clear Filters</p>
          )}
        </div>
      </div>
      <div className='flex items-center justify-between pl-5 pr-3 border-b border-gray-200 h-[3.75rem]'>
        <Pagination perPage={PAGE_SIZE} totalRecords={totalRecords} onPageChange={setCurrentPage} currentPage={currentPage} />
      </div>
      <div className='bg-[#FAFAFA] w-full'>
        <div className="ag-theme-alpine px-4 w-full h-1/2">
          <AgGridReact
            columnDefs={columnDefs as any}
            rowData={rowData || []}
            rowSelection="multiple"
            pagination={false}
            domLayout="autoHeight"
            loadingOverlayComponent={Loader}
            noRowsOverlayComponent={() => (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="text-lg font-medium">No Data Found</div>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <Filters _ref={filtersRef} config={filterConfig} filters={filters} onUpdate={setFilters} />
      </EntityGuard>
    </Layout>
  );
};

export default SparePartsUsageReport;
